import React, { useCallback, useEffect, useRef } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Highlight from '@tiptap/extension-highlight';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { toast } from 'sonner';
import type { Item } from '@/stores/canvasStore';
import { noteBody, noteBodyToHtml } from '@/lib/library/noteBody';
import { cn } from '@/lib/utils';

/** Idle time after the last keystroke before the note is written to the store. */
const SAVE_DELAY = 700;
/** Air kept between the caret and the top bar or the keyboard while typing. */
const CARET_TOP = 80;
const CARET_BOTTOM = 32;

/**
 * The note in the phone's detail view, editable in place. A tap on the text
 * puts the caret there (the editor is already mounted, so iOS brings the
 * keyboard up in the same tap). Same schema as the desktop text card
 * (StarterKit, which carries link and underline in v3, plus highlight and
 * task lists), so a note formatted on desktop keeps its formatting when it
 * is edited here.
 *
 * Saves after a pause in typing, and at once on blur, when the view closes
 * or pages away (unmount) and when the app goes to the background. An empty
 * note is not saved: clearing everything and leaving restores the text, so a
 * slip of select-all can't wipe a note (Delete removes one on purpose).
 */
export function NoteEditor({
  item,
  editable,
  keyboardInset = 0,
  onEditingChange,
  onSave,
  className,
}: {
  item: Item;
  /** False while the view flies open or shut, and for the neighbours drawn beside it. */
  editable: boolean;
  /** Pixels the on-screen keyboard covers; the caret is kept above it. */
  keyboardInset?: number;
  onEditingChange?: (editing: boolean) => void;
  /** The new body (TipTap HTML). Only called with a changed, non-empty note. */
  onSave?: (body: string) => void;
  className?: string;
}) {
  const body = noteBody(item);
  // The body this editor last loaded or saved; anything else arrived from outside
  const savedRef = useRef(body);
  // What has been typed since the last save, or null when nothing is waiting
  const pendingRef = useRef<{ html: string; empty: boolean } | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const focusedRef = useRef(false);

  const onSaveRef = useRef(onSave);
  const onEditingChangeRef = useRef(onEditingChange);
  useEffect(() => {
    onSaveRef.current = onSave;
    onEditingChangeRef.current = onEditingChange;
  }, [onSave, onEditingChange]);

  const save = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const pending = pendingRef.current;
    if (!pending || pending.empty) return;
    pendingRef.current = null;
    if (pending.html === savedRef.current) return;
    savedRef.current = pending.html;
    onSaveRef.current?.(pending.html);
  }, []);

  const editor = useEditor({
    immediatelyRender: true,
    shouldRerenderOnTransaction: false,
    editable,
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        link: { openOnClick: false },
      }),
      Highlight,
      TaskList,
      TaskItem.configure({ nested: true }),
      Placeholder.configure({ placeholder: 'Write something' }),
    ],
    content: noteBodyToHtml(body),
    editorProps: {
      attributes: {
        'aria-label': 'Note',
        class: cn('library-note outline-none', className),
        // Phone keyboards: sentence case and autocorrect, as in Notes
        autocapitalize: 'sentences',
        autocorrect: 'on',
        spellcheck: 'true',
      },
    },
    onUpdate: ({ editor }) => {
      pendingRef.current = { html: editor.getHTML(), empty: editor.isEmpty };
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(save, SAVE_DELAY);
    },
    onFocus: () => {
      focusedRef.current = true;
      onEditingChangeRef.current?.(true);
    },
    onBlur: ({ editor }) => {
      focusedRef.current = false;
      if (pendingRef.current?.empty) {
        pendingRef.current = null;
        if (timerRef.current) clearTimeout(timerRef.current);
        editor.commands.setContent(noteBodyToHtml(savedRef.current), { emitUpdate: false });
        toast("An empty note isn't saved. Use Delete to remove it.");
      } else {
        save();
      }
      onEditingChangeRef.current?.(false);
    },
  });

  // Flying open or shut, or paged away from: stop editing first, which saves
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    if (!editable && editor.isFocused) editor.commands.blur();
    editor.setEditable(editable, false);
  }, [editor, editable]);

  // A body changed from outside (desktop, Partnr, a refetch) while not typing here: show it
  useEffect(() => {
    if (!editor || editor.isDestroyed || focusedRef.current || pendingRef.current || body === savedRef.current) return;
    savedRef.current = body;
    editor.commands.setContent(noteBodyToHtml(body), { emitUpdate: false });
  }, [editor, body]);

  // Keep the caret clear of the keyboard and the top bar. The detail view's
  // scroller spans the whole screen, under the keyboard, so ProseMirror needs
  // to be told how much of it is covered.
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const edges = { top: CARET_TOP, bottom: keyboardInset + CARET_BOTTOM, left: 0, right: 0 };
    editor.view.setProps({ scrollThreshold: edges, scrollMargin: edges });
    if (keyboardInset > 0 && editor.isFocused) editor.commands.scrollIntoView();
  }, [editor, keyboardInset]);

  // The app going to the background (switching apps, locking the phone) saves what was typed
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') save();
    };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', save);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', save);
    };
  }, [save]);

  // Closing the view or paging to another card unmounts this: nothing typed is lost
  useEffect(() => () => save(), [save]);

  return <EditorContent editor={editor} />;
}
